import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, ProgressBar, Spinner, Badge, Alert } from 'react-bootstrap';
import subscriptionService from '../../services/subscription.service';

const UsageStats = () => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  
  // Default free plan usage stats - using useMemo to avoid dependency changes
  const defaultUsage = useMemo(() => ({
    clicks: { used: 0, limit: 1000, percentage: 0 },
    conversions: { used: 0, limit: 50, percentage: 0 }
  }), []);
  
  useEffect(() => {
    const fetchUsage = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      
      // Set timeout to handle very slow API responses
      const timeoutId = setTimeout(() => {
        if (loading) {
          console.log('Usage stats fetch timeout - using default data');
          setUsage(defaultUsage);
          setLoading(false);
        }
      }, 3000); // 3 second timeout for better UX
      
      try {
        // Use the subscription service's getUsageStats method
        const response = await subscriptionService.getUsageStats();
        
        if (response.data.status === 'success') {
          setUsage(response.data.data);
        } else {
          // Fallback to default usage if there's an API error
          console.error('API error:', response.data.message);
          setUsage(defaultUsage);
        }
      } catch (error) {
        console.error('Error fetching usage stats:', error);
        // Set default usage stats
        setUsage(defaultUsage);
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };
    
    fetchUsage();
  }, [currentUser, loading, defaultUsage]);
  
  if (loading) {
    return (
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <h5 className="mb-0">Monthly Usage</h5>
        </Card.Header>
        <Card.Body className="text-center py-3">
          <Spinner animation="border" variant="primary" size="sm" />
          <p className="mt-2 mb-0">Loading usage statistics...</p>
        </Card.Body>
      </Card>
    );
  }
  
  if (!usage) {
    // Instead of returning null, show default usage stats
    setUsage(defaultUsage);
    return null;
  }
  
  const { clicks, conversions } = usage;
  
  const getVariant = (percentage) => {
    if (percentage >= 90) return 'danger';
    if (percentage >= 75) return 'warning';
    return 'primary';
  };
  
  return (
    <Card className="mb-4 shadow-sm border-0">
      <Card.Header className="bg-primary text-white">
        <h5 className="mb-0"><i className="bi bi-graph-up me-2"></i>Monthly Usage</h5>
      </Card.Header>
      <Card.Body className="p-4">
        
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">
              <i className="bi bi-cursor-fill me-2 text-primary"></i>
              Link Clicks
            </h6>
            <Badge bg={clicks.limit === -1 ? "success" : (clicks.percentage > 80 ? "warning" : "primary")} className="p-2">
              <span className="fw-bold">{clicks.used}</span> / {clicks.limit === -1 ? '∞' : clicks.limit}
            </Badge>
          </div>
          {clicks.limit > 0 && (
            <div className="mt-2">
              <ProgressBar 
                variant={getVariant(clicks.percentage)} 
                now={clicks.percentage} 
                label={clicks.percentage > 5 ? `${clicks.percentage}%` : null}
                style={{height: '8px'}}
                className="shadow-sm"
              />
            </div>
          )}
          {clicks.limit === -1 && (
            <div className="mt-2 text-success">
              <i className="bi bi-infinity me-1"></i>
              <small>Unlimited with your Pro subscription</small>
            </div>
          )}
        </div>
        
        <div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">
              <i className="bi bi-cart-check-fill me-2 text-primary"></i>
              Conversions
            </h6>
            <Badge bg={conversions.limit === -1 ? "success" : (conversions.percentage > 80 ? "warning" : "primary")} className="p-2">
              <span className="fw-bold">{conversions.used}</span> / {conversions.limit === -1 ? '∞' : conversions.limit}
            </Badge>
          </div>
          {conversions.limit > 0 && (
            <div className="mt-2">
              <ProgressBar 
                variant={getVariant(conversions.percentage)} 
                now={conversions.percentage}
                label={conversions.percentage > 5 ? `${conversions.percentage}%` : null}
                style={{height: '8px'}}
                className="shadow-sm"
              />
            </div>
          )}
          {conversions.limit === -1 && (
            <div className="mt-2 text-success">
              <i className="bi bi-infinity me-1"></i>
              <small>Unlimited with your Pro subscription</small>
            </div>
          )}
        </div>
        
        {clicks.limit > 0 && clicks.percentage > 80 && (
          <Alert variant="warning" className="mt-3 mb-0 py-2">
            <small>
              <strong>Approaching your limit!</strong> Consider upgrading to our Pro plan for unlimited usage.
            </small>
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
};

export default UsageStats;
